import React, { useContext, useEffect, useState, useRef } from 'react'
import PageTitle from '../../../Components/PageTitle'
import { ConfigContext } from '../../../Context/ConfigContext'
import { GlobalLimitChanger, SubmitBtn } from '../../../Components/InputElements'
import { ContentLoader, NoRecords, TableRows } from '../../../Components/Shimmer'
import axios from 'axios'
import Swal from 'sweetalert2'
import Select from 'react-select'

const ManageProductCategories = () => {
    const { primaryColor, apiHeaderJson, apiURL } = useContext(ConfigContext)
    const headersJson = apiHeaderJson;
    const [isInfoLoading, setIsInfoLoading] = useState(false);

    // Form states
    const [productId, setProductId] = useState('')
    const [categoryId, setCategoryId] = useState('')
    const [productCategoryId, setProductCategoryId] = useState('')

    // Select options states
    const [productOptions, setProductOptions] = useState([])
    const [categoryOptions, setCategoryOptions] = useState([])

    // List states
    const [data, setData] = useState([])
    const [next, setNext] = useState(false)
    const [prev, setPrev] = useState(false)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [totalRecords, setTotalRecords] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [loading, setLoading] = useState(true)
    const [submitLoading, setSubmitLoading] = useState(false)
    const [keyword, setKeyword] = useState('')
    const [errors, setErrors] = useState({})

    // Load product options for react-select
    const loadProductOptions = async () => {
        try {
            const response = await axios.get(`${apiURL}Masters/GetProductList`, {
                headers: headersJson
            })
            const { success, data } = response.data

            if (success) {
                const options = data.map(product => ({
                    value: product.product_id,
                    label: product.product_title
                }))
                setProductOptions(options)
            }
        } catch (error) {
            console.log("error loading products", error)
            Swal.fire('Error!', 'Failed to load products', 'error')
        }
    }

    // Load category options for react-select
    const loadCategoryOptions = async () => {
        try {
            const response = await axios.get(`${apiURL}Masters/GetCategoryList`, {
                headers: headersJson
            })
            const { success, data } = response.data

            if (success) {
                const options = data.map(category => ({
                    value: category.category_id,
                    label: category.category_title
                }))
                setCategoryOptions(options)
            }
        } catch (error) {
            console.log("error loading categories", error)
            Swal.fire('Error!', 'Failed to load categories', 'error')
        }
    }

    // Form validation
    const validateForm = () => {
        const errors = {}

        if (!productId) {
            errors.productId = "Product selection is required"
        }
        if (!categoryId) {
            errors.categoryId = "Category selection is required"
        }

        setErrors(errors)
        return Object.keys(errors).length === 0
    }

    // Reset form
    const resetForm = () => {
        setProductId('')
        setCategoryId('')
        setProductCategoryId('')
        setErrors({})
    }

    // Form submission
    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validateForm()) return

        setSubmitLoading(true)
        try {
            const formData = new FormData()
            formData.append('product_id', productId)
            formData.append('category_id', categoryId)

            if (productCategoryId) {
                formData.append('product_category_id', productCategoryId)
            }

            const response = await axios.post(`${apiURL}Masters/CreateProductCategory`, formData, { headers: headersJson })
            const { message, success } = response.data

            if (success) {
                resetForm()
                getData()
                Swal.fire('Success!', message, 'success')
            } else {
                Swal.fire('Error!', message || 'Something went wrong', 'error')
            }
        } catch (error) {
            console.log("error", error)
            Swal.fire('Error!', 'Something went wrong', 'error')
        } finally {
            setSubmitLoading(false)
        }
    }

    // Get product categories list
    const getData = async () => {
        try {
            setLoading(true)
            const response = await axios.get(`${apiURL}Masters/GetProductCategories`, {
                headers: headersJson,
                params: {
                    keyword,
                    page,
                    limit
                }
            })
            const { success, data, page: currentPage, next, prev, total_pages, total_records } = response.data

            if (success) {
                setData(data)
                setPage(currentPage)
                setNext(next)
                setPrev(prev)
                setTotalPages(total_pages)
                setTotalRecords(total_records)
            }
        } catch (error) {
            console.log("error", error)
            Swal.fire('Error!', 'Failed to fetch product categories', 'error')
        } finally {
            setLoading(false)
        }
    }

    // Get single product category info for editing
    const getInfo = async (product_category_id) => {
        try {
            setIsInfoLoading(true);
            const response = await axios.get(`${apiURL}Masters/GetProductCategoryInfo`, {
                headers: headersJson,
                params: { product_category_id: product_category_id }
            })
            const { success, data } = response.data

            if (success && data[0]) {
                const productCategory = data[0]
                setProductId(productCategory.product_id)
                setCategoryId(productCategory.category_id)
                setProductCategoryId(productCategory.product_category_id)
                setErrors({})
            }
        } catch (error) {
            console.log("error", error)
            Swal.fire('Error!', 'Failed to load product category info', 'error')
        } finally {
            setIsInfoLoading(false);
        }
    }

    // Delete product category
    const handleDelete = async (product_category_id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        })
        if (result.isConfirmed) {
            setLoading(true)
            try {
                const body = { product_category_id: product_category_id }
                const response = await axios.post(`${apiURL}Masters/DeleteProductCategory`, body, { headers: headersJson })
                const { message, success } = response.data

                Swal.fire('Deleted!', message, success ? 'success' : 'error')

                if (success) {
                    getData()
                }
            } catch (error) {
                console.error(error)
                Swal.fire('Error!', 'Something went wrong', 'error')
            } finally {
                setLoading(false)
            }
        }
    }

    // Pagination handlers
    const handlePrev = () => {
        if (prev) {
            setPage(prev => prev - 1)
        }
    }

    const handleNext = () => {
        if (next) {
            setPage(prev => prev + 1)
        }
    }

    const handlePageChange = (e) => {
        setPage(parseInt(e.target.value, 10))
    }

    // Load options on component mount
    useEffect(() => {
        loadProductOptions()
        loadCategoryOptions()
    }, [])

    // Search with debounce
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setPage(1)
            getData()
        }, 500)

        return () => clearTimeout(delayDebounce)
    }, [keyword])

    // Pagination effect
    useEffect(() => {
        getData()
    }, [limit, page])

    return (
        <>
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <PageTitle title="Manage Product Categories" primary="Masters" />
                        {isInfoLoading && <ContentLoader />}
                        <div className="row">
                            <div className="col-md-4">
                                <div className="card">
                                    <div className="card-header" style={{ backgroundColor: primaryColor }}>
                                        <h4 className="card-title mb-0 text-white">
                                            {productCategoryId ? 'Update Product Category' : 'New Product Category'}
                                        </h4>
                                    </div>
                                    <div className="card-body">
                                        <form onSubmit={handleSubmit}>
                                            <div className="row">
                                                {/* Product Selection */}
                                                <div className="col-md-12 mb-3">
                                                    <label htmlFor="productId" className="form-label">
                                                        Product: <span className="text-danger">*</span>
                                                    </label>
                                                    <Select
                                                        id="productId"
                                                        name="productId"
                                                        options={productOptions}
                                                        value={productOptions.find(option => option.value === productId) || null}
                                                        onChange={(selectedOption) => {
                                                            setProductId(selectedOption?.value || '')
                                                            if (errors.productId) {
                                                                setErrors({ ...errors, productId: "" })
                                                            }
                                                        }}
                                                        placeholder="Select a product..."
                                                        isSearchable
                                                        isClearable
                                                        className={` ${errors.productId ? 'is-invalid' : ''}`}
                                                    />
                                                    {errors.productId && <div className="invalid-feedback d-block">{errors.productId}</div>}
                                                </div>

                                                {/* Category Selection */}
                                                <div className="col-md-12 mb-3">
                                                    <label htmlFor="categoryId" className="form-label">
                                                        Category: <span className="text-danger">*</span>
                                                    </label>
                                                    <Select
                                                        id="categoryId"
                                                        name="categoryId"
                                                        options={categoryOptions}
                                                        value={categoryOptions.find(option => option.value === categoryId) || null}
                                                        onChange={(selectedOption) => {
                                                            setCategoryId(selectedOption?.value || '')
                                                            if (errors.categoryId) {
                                                                setErrors({ ...errors, categoryId: "" })
                                                            }
                                                        }}
                                                        placeholder="Select a category..."
                                                        isSearchable
                                                        isClearable
                                                        className={`${errors.categoryId ? 'is-invalid' : ''}`}
                                                    />
                                                    {errors.categoryId && <div className="invalid-feedback d-block">{errors.categoryId}</div>}
                                                </div>

                                                {/* Submit Buttons */}
                                                <div className="col-md-12 mb-3">
                                                    <div className='col-md-12 d-flex align-items-end justify-content-end gap-3 mt-3'>
                                                        {productCategoryId ? (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-light btn-label right me-auto"
                                                                    onClick={resetForm}
                                                                >
                                                                    Reset
                                                                    <i className="ri-arrow-right-line label-icon align-middle fs-16" />
                                                                </button>
                                                                <SubmitBtn
                                                                    icon="ri-save-line"
                                                                    text="Update"
                                                                    onClick={handleSubmit}
                                                                    type="primary"
                                                                />
                                                            </>
                                                        ) : (
                                                            <SubmitBtn
                                                                disabled={submitLoading}
                                                                text={submitLoading ? 'Saving..' : 'Save'}
                                                                type="primary"
                                                                onClick={handleSubmit}
                                                                icon="ri-save-line"
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-8">
                                <div className="card">
                                    <div className="card-header" style={{ backgroundColor: primaryColor }}>
                                        <div className="row d-flex align-items-center">
                                            <div className="col-md-7">
                                                <h4 className="card-title mb-0 text-white">Product Categories List</h4>
                                            </div>
                                            <div className="col-md-5">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control pe-4"
                                                        placeholder="Search product categories..."
                                                        name='keyword'
                                                        value={keyword}
                                                        onChange={(e) => setKeyword(e.target.value)}
                                                    />
                                                    <span className="position-absolute end-0 top-50 translate-middle-y me-3">
                                                        <i className="ri-search-line"></i>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="table-responsive table-card">
                                            <table className="table table-bordered table-striped table-nowrap table-hover mb-0">
                                                {loading ? (
                                                    <TableRows rows="10" colspan="4" />
                                                ) : (
                                                    <>
                                                        <thead className="table-light text-center">
                                                            <tr>
                                                                <th>#</th>
                                                                <th>Product</th>
                                                                <th>Category</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {data.length > 0 ? (
                                                                data.map((row, index) => (
                                                                    <tr key={row.product_category_id} className="text-center">
                                                                        <td>{row.product_category_id}</td>
                                                                        <td className="text-start">
                                                                            <strong>{row.product_title}</strong>
                                                                        </td>
                                                                        <td className="text-start">
                                                                            <div className="d-flex align-items-center">
                                                                                <strong>{row.category_title}</strong>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            <button
                                                                                className="btn btn-sm btn-soft-primary me-2"
                                                                                onClick={() => getInfo(row.product_category_id)}
                                                                                title="Edit Product Category"
                                                                            >
                                                                                <i className='ri-pencil-line'></i>
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-sm btn-soft-danger"
                                                                                onClick={() => handleDelete(row.product_category_id)}
                                                                                title="Delete Product Category"
                                                                            >
                                                                                <i className='ri-delete-bin-line'></i>
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan={4}>
                                                                        <NoRecords />
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                        <tfoot className='table-light'>
                                                            <tr>
                                                                <th colSpan={4}>
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <button disabled={!prev || loading} type="button" onClick={handlePrev} className={`btn btn-warning btn-label waves-effect waves-light`}>
                                                                            <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2" /> Previous
                                                                        </button>

                                                                        <div className='d-flex align-items-center gap-3'>
                                                                            <small>
                                                                                Total Records: {totalRecords} |
                                                                                Total Pages: {totalPages} |
                                                                                Current Page: {page}
                                                                            </small>

                                                                            <select
                                                                                className="form-select"
                                                                                value={page}
                                                                                onChange={handlePageChange}
                                                                                style={{ width: 'auto' }}
                                                                            >
                                                                                {Array.from({ length: totalPages }, (_, i) => (
                                                                                    <option key={i + 1} value={i + 1}>
                                                                                        {i + 1}
                                                                                    </option>
                                                                                ))}
                                                                            </select>

                                                                            <GlobalLimitChanger
                                                                                placeholder="Set limit:"
                                                                                name="globalLimit"
                                                                                value={limit}
                                                                                onChange={setLimit}
                                                                                showAllValue={totalRecords}
                                                                            />
                                                                        </div>

                                                                        <button disabled={!next || loading} type="button" onClick={handleNext} className={`btn btn-primary btn-label waves-effect right waves-light`}>
                                                                            <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2" /> Next
                                                                        </button>
                                                                    </div>
                                                                </th>
                                                            </tr>
                                                        </tfoot>
                                                    </>
                                                )}
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ManageProductCategories;